import * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";
import { cn } from "@marketing-sites/utils/cn";
import DotDecorators from "../../components/dot-decorators";
import SectionTags, {
  type SectionTagsProps,
} from "../../components/section-tags";

const sectionVariants = tv({
  base: "col-span-full grid grid-cols-subgrid",
  variants: {
    gridCols: {
      "12": "",
      "10": "md:col-span-10 md:col-start-2",
      "8": "md:col-span-8 md:col-start-3",
    },
  },
  defaultVariants: {
    gridCols: "12",
  },
});

type SectionVariantProps = VariantProps<typeof sectionVariants>;

export interface SectionProps
  extends React.ComponentPropsWithRef<"section">,
    SectionVariantProps {
  id: string;
  "data-analytics-region": string;
  dotDecorators?: boolean;
  /** Extra classes forwarded to the section's corner DotDecorators — e.g. to
   *  recolour them (`text-helpdesk`). Has no effect when `dotDecorators` is false. */
  dotDecoratorsClassName?: string;
  /** Section shell's tag row (left page label / right section label), rendered
   *  as the first row of the subgrid. Omit to render no row; a half-empty pair
   *  renders nothing. */
  tags?: SectionTagsProps;
  /** "grid" (default) places children directly on the section's subgrid, so
   *  each child needs its own `col-span-*` placement — an unspanned child
   *  collapses to a single column. "block" wraps children in one full-width
   *  block for components that manage their own internal layout. */
  layout?: "grid" | "block";
}

export function Section({
  dotDecorators = true,
  dotDecoratorsClassName,
  tags,
  gridCols = "12",
  layout = "grid",
  className,
  children,
  ...rest
}: SectionProps) {
  return (
    <section className={cn("container relative mx-auto", className)} {...rest}>
      <div className="grid grid-cols-12 gap-x-4">
        <div className={sectionVariants({ gridCols })}>
          {tags && <SectionTags {...tags} />}
          {layout === "block" ? (
            <div className="col-span-full">{children}</div>
          ) : (
            children
          )}
        </div>
      </div>
      {dotDecorators && <DotDecorators className={dotDecoratorsClassName} />}
    </section>
  );
}
